import { supabase } from './supabaseClient';

export interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: 'super_admin' | 'admin' | 'moderator';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Sign up new admin using Edge Function (bypasses RLS issues)
export const signUpAdmin = async (email: string, password: string, fullName?: string) => {
  try {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    
    // Call Edge Function to create admin account
    const response = await fetch(`${supabaseUrl}/functions/v1/admin-signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        fullName
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create admin account');
    }

    return data;
  } catch (error: any) {
    console.error('Admin signup error:', error);
    throw error;
  }
};

// Sign in admin
export const signInAdmin = async (email: string, password: string) => {
  try {
    console.log('Starting admin sign in for:', email);
    
    // Sign in with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (authError) {
      console.error('Auth error:', authError);
      
      // Provide more specific error messages
      if (authError.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      } else if (authError.message.includes('Email not confirmed')) {
        throw new Error('Please verify your email address before signing in.');
      } else {
        throw new Error(authError.message || 'Failed to sign in');
      }
    }
    
    if (!authData.user || !authData.session) {
      throw new Error('Failed to sign in - no user or session returned');
    }

    console.log('Auth successful, user ID:', authData.user.id);

    // Wait a moment for session to be fully established
    await new Promise(resolve => setTimeout(resolve, 300));

    // Check if user is an admin using Edge Function
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    
    console.log('Checking admin status...');
    
    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/admin-auth-helper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData.session.access_token}`
        },
        body: JSON.stringify({
          action: 'check_admin',
          userId: authData.user.id
        })
      });

      const adminCheck = await response.json();
      console.log('Admin check response:', adminCheck);

      if (!response.ok || !adminCheck.isAdmin) {
        console.error('Not an admin user');
        await supabase.auth.signOut();
        throw new Error('This account does not have admin privileges. Please contact support if you believe this is an error.');
      }

      // Store admin info in localStorage
      localStorage.setItem('admin_user', JSON.stringify(adminCheck.admin));
      localStorage.setItem('admin_session', authData.session.access_token);
      localStorage.setItem('admin_authenticated', 'true');

      console.log('Admin login successful');

      return { user: authData.user, admin: adminCheck.admin, session: authData.session };
    } catch (fetchError: any) {
      console.error('Admin check failed:', fetchError);
      await supabase.auth.signOut();
      throw new Error('Failed to verify admin status. Please try again or contact support.');
    }
  } catch (error: any) {
    console.error('Admin signin error:', error);
    throw error;
  }
};

// Sign out admin
export const signOutAdmin = async () => {
  try {
    await supabase.auth.signOut();
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_session');
    localStorage.removeItem('admin_authenticated');
  } catch (error: any) {
    console.error('Admin signout error:', error);
    throw error;
  }
};

// Get current admin user
export const getCurrentAdmin = async (): Promise<AdminUser | null> => {
  try {
    // First check localStorage for cached admin data
    const cachedAdmin = localStorage.getItem('admin_user');
    const isAuthenticated = localStorage.getItem('admin_authenticated');
    
    if (!cachedAdmin || isAuthenticated !== 'true') {
      console.log('No cached admin data found');
      return null;
    }

    // Verify session is still valid
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.log('No active session found');
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_session');
      localStorage.removeItem('admin_authenticated');
      return null;
    }

    // Return cached admin data if session is valid
    return JSON.parse(cachedAdmin);
  } catch (error) {
    console.error('Get current admin error:', error);
    return null;
  }
};

// Check if user is authenticated admin
export const isAdminAuthenticated = async (): Promise<boolean> => {
  try {
    // Quick check for authentication flag
    const isAuthenticated = localStorage.getItem('admin_authenticated');
    if (isAuthenticated !== 'true') {
      return false;
    }

    // Verify session is still valid
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_session');
      localStorage.removeItem('admin_authenticated');
      return false;
    }

    const admin = await getCurrentAdmin();
    return admin !== null && admin.is_active;
  } catch (error) {
    console.error('Admin authentication check error:', error);
    return false;
  }
};

// Update admin profile
export const updateAdminProfile = async (updates: Partial<AdminUser>) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('admin_users')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;

    // Update localStorage
    localStorage.setItem('admin_user', JSON.stringify(data));

    return data;
  } catch (error: any) {
    console.error('Update admin profile error:', error);
    throw error;
  }
};
